<template>
  <div>
    <loading-overlay :value="loading" />
    <div class="pb-4" v-if="ownersAndManagingEmployees">
      <h4 class="pb-2">
        List names, addresses, Dates of Birth and SSN for all Persons with an
        ownership interest in; or who are Managing Employees of the Disclosing
        Entity.
      </h4>
      <div>
        An <strong>ownership</strong> interest is any entity that has direct or
        indirect ownership interest in the disclosing entity with ownership of 5
        percent or more.
      </div>
      <div>
        <strong>Managing employees</strong> are people who exercise operational
        or managerial control over, or who directly or indirectly conduct the
        day-to-day operations or head up the business functions of a Provider
        Entity. State and federal requirements prohibit a Medicaid MCO from
        contracting with a Provider Entity whose Managing Employees are excluded
        from federal healthcare programs.
      </div>
    </div>
    <v-data-table
      class="main-table"
      :headers="headers"
      :items="owners"
      hide-default-footer
      disable-pagination
      disable-sort
    >
      <!-- Note, we need items slot for row level class until https://github.com/vuetifyjs/vuetify/issues/8643 is implemented -->
      <template v-slot:item="{ item }">
        <tr :key="item.ownerId">
          <td>{{ getOwnerName(item) }}</td>
          <td>
            <span v-if="!ownerEditMode">
              {{ apiToDisplay(item.percentage) }}
            </span>
            <input
              v-model="item.tentativeOwnershipPercentage"
              v-if="ownerEditMode"
              class="ownership-percentage-input"
            />
          </td>
          <td class="right-border text-right">
            <v-btn
              depressed
              height="32px"
              width="32px"
              class="tertiary mr-4"
              title="Remove Owner"
              @click="initiateRemoval(item)"
            >
              <v-icon class="terminate-button-icon">$boldx</v-icon>
            </v-btn>
            <v-btn
              v-if="showOwnerDetailsButton"
              height="32px"
              min-width="32px"
              :to="{
                name: 'ownerDetails',
                params: { ownerId: item.ownerId, Where }
              }"
              @click.native="
                logEvent('Clicked button to view owner', { Where })
              "
              class="tertiary"
              depressed
            >
              <v-icon color="primary">mdi-arrow-right</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
      <template v-slot:body.append="">
        <tr class="tr-no-style">
          <td class="td-no-style align-right">
            <span v-if="ownerEditMode" class="ownership-total-label">
              Ownership Percentage Total:
            </span>
          </td>
          <td colspan="2" class="td-no-style">
            <span
              v-if="ownerEditMode"
              :class="{
                'total-ownership-percentage': true,
                'invalid-total': isPercentageInvalid
              }"
            >
              {{ totalOwnership }} %
            </span>

            <div class="float-sm-right mt-2">
              <div v-if="ownerEditMode">
                <v-btn
                  class="ownership-percentage-button"
                  text
                  :block="$vuetify.breakpoint.xsOnly"
                  @click="setEditMode(false)"
                >
                  <span class="edit-ownership-percentage-text">
                    Cancel
                  </span>
                </v-btn>
                <call-to-action
                  class="primary--text add-owner-button"
                  @click="saveOwnershipChanges"
                  :disabled="isPercentageInvalid"
                >
                  Save Changes
                </call-to-action>
              </div>

              <div v-if="!ownerEditMode">
                <v-btn
                  class="ownership-percentage-button"
                  text
                  :block="$vuetify.breakpoint.xsOnly"
                  @click="
                    logEvent('Clicked button to edit ownership percentage', {
                      Where
                    });
                    setEditMode(true);
                  "
                >
                  <span class="edit-ownership-percentage-text">
                    Edit Ownership Percentages
                  </span>
                </v-btn>
                <call-to-action
                  v-if="addOwnerInline"
                  class="primary--text add-owner-button"
                  @click="addOwnerInlineClicked"
                >
                  Add Owner
                </call-to-action>
                <call-to-action
                  v-else
                  class="primary--text add-owner-button"
                  @click.native="
                    logEvent('Clicked button to add Owners', { Where })
                  "
                  :to="{ name: 'addOwner' }"
                >
                  Add Owner
                </call-to-action>
              </div>
            </div>
          </td>
        </tr>
        <tr class="tr-no-style">
          <td class="td-no-style align-right"></td>
          <td colspan="2" class="td-no-style invalid-total-warning-container">
            <div v-if="isPercentageInvalid" class="invalid-total-warning ml-2">
              Total % must not exceed 100%
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>

    <add-owner
      :key="resetKey"
      v-if="displayAddOwnerInlineForm"
      :client-id="clientId"
      @cancel-clicked="dismissAddForm"
      @save-success="addOwnerSuccess"
    />
    <v-dialog v-model="deleteConfirmationDialogOpen" width="672">
      <v-card class="pa-4">
        <v-card-title class="justify-center pt-0">
          <v-icon size="48" color="colorAlertRed">mdi-alert-rhombus</v-icon>
          <v-btn
            icon
            absolute
            top
            right
            @click="deleteConfirmationDialogOpen = false"
          >
            <v-icon>mdi-window-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          Are you sure you want to remove this owner?
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelRemoval">
            Cancel
          </v-btn>

          <v-btn color="primary" @click="actuallyRemove">
            Remove Owner
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  clientConfigAPI,
  ownerByIdAPI,
  ownersAPI,
  ownershipAPI
} from "@/services/api";
import { _delete, get, put } from "@/services/request";
import BlueCallToAction from "@/components/providertrust/buttons/BlueCallToAction";
import hasNotifications from "@/mixins/has-notifications";
import AddOwner from "@/views/AddOwner";
import LoadingOverlay from "@/components/LoadingOverlay";

export default {
  name: "Owners",
  components: {
    LoadingOverlay,
    "call-to-action": BlueCallToAction,
    "add-owner": AddOwner
  },
  mixins: [hasNotifications],
  inject: ["Where"],
  props: {
    addOwnerInline: {
      type: Boolean,
      default: false
    },
    ownersAndManagingEmployees: {
      //triggers different text
      type: Boolean,
      default: false
    },
    showOwnerDetailsButton: {
      type: Boolean,
      default: true
    },
    clientId: {
      //If exists, used to load client-specific configuration
      type: Number,
      default: undefined
    }
  },
  data: () => ({
    loading: false,
    owners: [],
    headers: [
      { text: "Owner Name", value: "ownerName", width: "40%" },
      {
        text: "Ownership Percentage",
        value: "ownershipPercentage",
        width: "170px"
      },
      { text: " ", value: "action", align: "end" }
    ],
    ownerEditMode: false,
    ownerToDelete: null,
    deleteConfirmationDialogOpen: false,
    displayAddOwnerInlineForm: false,
    resetKey: 0,
    clientConfig: {}
  }),
  watch: {
    displayAddOwnerInlineForm() {
      //AddOwner form must be saved or dismissed before proceeding
      this.validate();
    },
    ownerEditMode() {
      //Edit Percentage form must be saved or dismissed before proceeding
      this.validate();
    },
    owners() {
      this.validate();
    }
  },
  created() {
    let loadOwners = this.loadOwners();
    let loadClientConfig = this.loadClientConfig();

    this.loading = true;
    Promise.all([loadOwners, loadClientConfig])
      .then(() => {
        this.loading = false;
      })
      .catch(() => {
        this.showMessage({
          text: `Error loading required data, please try again or contact us`,
          mode: "error"
        });
      });
  },
  methods: {
    setEditMode(val) {
      this.resetOwnershipPercentages();
      this.ownerEditMode = val;
    },
    resetOwnershipPercentages() {
      for (let i = 0; i < this.owners.length; i++) {
        this.$set(
          this.owners[i],
          "tentativeOwnershipPercentage",
          this.apiToDisplay(this.owners[i].percentage)
        );
      }
    },
    saveOwnershipChanges() {
      let ownershipPercentages = {};
      this.owners.forEach(o => {
        ownershipPercentages[o.ownerId] = this.displayToApi(
          o.tentativeOwnershipPercentage
        );
      });

      put(ownershipAPI, ownershipPercentages).then(response => {
        if (response) {
          this.showMessage(
            "You've successfully updated the Ownership Percentages."
          );
          for (let i = 0; i < this.owners.length; i++) {
            this.owners[i].percentage = this.displayToApi(
              this.owners[i].tentativeOwnershipPercentage
            );
          }
        }
      });

      this.ownerEditMode = false;
    },
    getOwnerName(owner) {
      if (owner.businessName) return owner.businessName;
      else return owner.firstName + " " + owner.lastName;
    },
    apiToDisplay(value) {
      return value != null ? value + " %" : "0 %";
    },
    displayToApi(value) {
      let str = value.match(/(\d+)/);
      return parseInt(str);
    },
    actuallyRemove() {
      this.deleteConfirmationDialogOpen = false;
      let owner = this.ownerToDelete;
      this.ownerToDelete = null;
      _delete(ownerByIdAPI.getUrl(owner.ownerId)).then(() => {
        this.owners = this.owners.filter(it => it !== owner);
        const ownerName = owner.businessName
          ? owner.businessName
          : `${owner.firstName} ${owner.lastName}`;
        this.showMessage(`${ownerName} was successfully removed.`).catch(() =>
          this.showMessage({
            text: `Failed to remove ${ownerName}`,
            mode: "error"
          })
        );
      });
    },
    initiateRemoval(ownerId) {
      this.deleteConfirmationDialogOpen = true;
      this.ownerToDelete = ownerId;
    },
    cancelRemoval() {
      this.deleteConfirmationDialogOpen = false;
      this.ownerToDelete = null;
    },
    addOwnerInlineClicked() {
      this.logEvent("Clicked button to add Owners", { Where: this.Where });
      this.displayAddOwnerInlineForm = true;
    },
    dismissAddForm() {
      this.displayAddOwnerInlineForm = false;
      this.resetKey += 1;
    },
    loadOwners() {
      return get(ownersAPI).then(response => {
        if (response && response.data) {
          this.owners = response.data;
          this.resetOwnershipPercentages();
        }
      });
    },
    loadClientConfig() {
      if (this.clientId) {
        return get(clientConfigAPI.getUrl(this.clientId)).then(response => {
          if (response && response.data) {
            this.clientConfig = response.data;
          }
        });
      } else {
        return Promise.resolve();
      }
    },
    addOwnerSuccess() {
      this.dismissAddForm();
      this.loadOwners();
    },
    validate() {
      const ownerRequirementMet = this.ownerRequired
        ? this.owners.length !== 0
        : true;
      const formValid =
        !this.displayAddOwnerInlineForm &&
        !this.ownerEditMode &&
        ownerRequirementMet;

      this.$emit("form-valid", formValid);
      if (!ownerRequirementMet) {
        this.$emit("form-error-message", "Please enter at least one owner");
      } else if (!formValid) {
        this.$emit(
          "form-error-message",
          "Please save your changes or cancel out of them"
        );
      }
    }
  },
  computed: {
    totalOwnership() {
      return this.owners
        .map(o => o.tentativeOwnershipPercentage)
        .map(pct => (pct != null ? pct.match(/(\d+)/) : pct))
        .map(pct => parseInt(pct))
        .filter(pct => !isNaN(pct))
        .reduce(function(a, b) {
          return a + b;
        }, 0);
    },
    isPercentageInvalid() {
      return (
        this.totalOwnership > 100 ||
        this.owners.some(o => isNaN(parseInt(o.tentativeOwnershipPercentage)))
      );
    },
    ownerRequired() {
      return !!this.clientConfig.requirementMap?.OWNER_REQUIRED_AT_SIGNUP;
    }
  }
};
</script>

<style lang="sass" scoped>
.v-btn
  font-weight: bold
  letter-spacing: unset
  text-indent: unset
  font-size: 1rem

.load-btn.action-btn
  min-width: 0

.add-owner-container
  margin-bottom: 40px

.edit-ownership-percentage-text
  font-weight: semibold
  font-size: 16px
  text-decoration: underline
  text-decoration-color: $darker-grey
  color: $lightish-grey

.ownership-percentage-button.v-btn::before
  background-color: transparent

.ownership-percentage-button
  & ::v-deep > .v-ripple__container
    display: none

.total-ownership-label
  width: 100%
  text-align: right

.align-right
  text-align: right

.ownership-total-label
  color: $lightish-grey
  font-size: 14px
  font-weight: 600
  line-height: 18px

.total-ownership-percentage
  color: $darker-grey
  font-size: 16px
  line-height: 36px
  padding-left: 8px
  width: 88px
  display: inline-block
  border-bottom: transparent solid 2px
  border-top: transparent solid 2px

.total-ownership-percentage.invalid-total
  border-bottom: $error solid 2px

.invalid-total-warning
  color: $error
  font-size: 12px
  line-height: 12px

.invalid-total-warning-container
  padding: 0 8px
  position: relative
  top: -8px

.ownership-percentage-input
  height: 32px
  width: 88px
  border-radius: 2px
  background-color: $howmany-grey
  padding: 8px

.tr-no-style
  background-color: transparent !important
  height: 32px

.td-no-style
  border: transparent solid 0
  border-bottom: transparent solid 0
  height: 32px

.theme--light.v-data-table tbody tr:not(:last-child) td.td-no-style:not(.v-data-table__mobile-row)
  border: transparent solid 0 !important

.tertiary.v-btn:not(.v-btn--round).v-size--default
  padding: 0
  min-width: 0
</style>
